import { Injectable } from '@angular/core';
import { CUSTOMER_FAMILY_ROLE, CUSTOMER_MEMBER_ROLE, FAMILY_ROLE } from '@careapp-group/core';
import { CustomersService, InvitationsService } from '@careapp-group/ng-sdk-v2-new';
import { Actions, createEffect, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { format, parse } from 'date-fns';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  throttle,
  throttleTime,
  withLatestFrom,
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { GoogleGeocodeService } from '../../shared/services/index';
import { CAAction, Organisations } from '../actions';
import * as selectors from '../reducers/index';
import { org } from '../reducers/index';
import { Customers } from './customers.actions';
import { AddUrlDto } from '@careapp-group/ng-sdk-v2-new/model/addUrlDto';
import { getCustomerPaginators } from './customers.selectors';
import { PageEvent } from '@angular/material/paginator';
import { INITIAL_DATA_LIST_STATE } from '../data-list.state';

@Injectable()
export class CustomersEffects {
  @Effect()
  paginate = this.actions$.pipe(
    ofType(Customers.PAGE_CHANGE_BY_ID),
    filter((action) => action.payload.paginatorId != null),
    map((action) => {
      const paginatorId = action.payload.paginatorId;
      return Customers.RequestAllPaginated({
        paginatorId,
        clear: true,
      });
    })
  );

  @Effect()
  updateFilter = this.actions$.pipe(
    ofType(Customers.UPDATE_FILTER_BY_ID),
    filter((action) => action.payload.paginatorId != null),
    map((action) => {
      const paginatorId = action.payload.paginatorId;
      return Customers.RequestAllPaginated({
        paginatorId,
        clear: true,
      });
    })
  );

  @Effect()
  getCustomerById = this.actions$.pipe(
    ofType(Customers.REQUEST_BY_ID),
    withLatestFrom(this.store.select(selectors.getUpdatedAt('customers'))),
    mergeMap(([action, updatedAt]) => {
      if (
        !action.payload.forceRefresh &&
        updatedAt &&
        updatedAt[action.payload.id] &&
        updatedAt[action.payload.id] > new Date().valueOf() - environment.cacheTime
      ) {
        return of(Customers.ResponseSuccessByIdActionCached(action.payload));
      }
      return this.customerService.findById({ id: action.payload.id }).pipe(
        map((res: any) => Customers.ResponseSuccessByIdAction(res)),
        catchError((err) => {
          return of(Customers.ResponseFailAction(err));
        })
      );
    })
  );

  getCustomersPaginated = createEffect(() =>
    this.actions$.pipe(
      ofType(Customers.RequestAllPaginated.type),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.TENANTS)),
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
        this.store.select(getCustomerPaginators)
      ),
      switchMap(([action, tenantId, organisationId, paginators]) => {
        const typedAction: ReturnType<typeof Customers.RequestAllPaginated> = action;
        const page: PageEvent =
          paginators[typedAction.paginatorId]?.page ?? INITIAL_DATA_LIST_STATE.page;
        const filter: { name?: string; externalId?: string; limit?: number } =
          paginators[typedAction.paginatorId]?.filter ?? {};
        return this.customerService
          .listCustomers(
            {
              organisationId,
              tenantId,
              limit: `${page.pageSize}`,
              skip: `${page.pageSize * page.pageIndex}`,
              externalId: filter.externalId != null ? filter.externalId : undefined,
              name: filter.name != null ? filter.name : undefined,
              enabled: undefined,
            },
            'response'
          )
          .pipe(
            switchMap((response) => {
              return [
                ...(typedAction.clear === true
                  ? [Customers.ClearPaginationIdsAction(typedAction.paginatorId)]
                  : []),
                Customers.ResponseSuccessAllPaginated({
                  filter,
                  items: response.body ?? [],
                  paginator: typedAction.paginatorId,
                  page: {
                    ...page,
                    length: parseInt(response?.headers?.get('x-total-count') ?? '0'),
                  },
                }),
              ];
            }),
            catchError((err) =>
              of(Customers.ResponseFailAllPaginatedAction(err, typedAction.paginatorId))
            )
          );
      })
    )
  );

  // This will affect the entire entities list. Use RequestAllPaginated if possible.
  @Effect()
  getCustomers = this.actions$.pipe(
    ofType(Customers.REQUEST_ALL),
    withLatestFrom(
      this.store.select(selectors.getSelectedId(selectors.TENANTS)),
      this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
      this.store.select(selectors.getPageination(selectors.CUSTOMERS)),
      this.store.select(selectors.getFilter(selectors.CUSTOMERS))
    ),
    switchMap(([action, tenantId, organisationId, pagination, filter]) => {
      return this.customerService
        .listCustomers(
          {
            tenantId,
            organisationId,
            limit: action.payload.skipPagination ? undefined : `${pagination.pageSize}`,
            skip: action.payload.skipPagination
              ? undefined
              : `${pagination.pageSize * pagination.pageIndex}`,
            externalId: filter.externalId,
            name: filter.name,
            enabled: undefined,
          },
          'response'
        )
        .pipe(
          switchMap((response) => {
            return [
              Customers.ResponseSuccessAllAction(response.body),
              Customers.ResponseSuccessCountAction(
                parseInt(response?.headers?.get('x-total-count') ?? '0')
              ),
            ];
          }),
          catchError((err) => of(Customers.ResponseFailAllAction(err)))
        );
    })
  );

  @Effect()
  createCustomer = this.actions$.pipe(
    ofType<CAAction>(Customers.REQUEST_CREATE),
    withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
    switchMap(([action, organisationId]) => {
      const details = { ...action.payload, organisationId };
      if (!details.location || details.location.lat === '' || details.location.lng === '') {
        details.location = null;
      }
      return this.customerService.createCustomer({ createCustomerRequestDto: details }).pipe(
        map((payload: any) => {
          return Customers.ResponseSuccessCreateAction(payload);
        }),
        catchError((err) => of(Customers.ResponseFailCreateAction(err)))
      );
    })
  );

  parseDate(date: string): string | null {
    const expectedFormat = 'yyyy-MM-dd';

    // Add any unexpected formats here.
    const possibleFormats = [expectedFormat, 'yyyy/MM/dd'];

    if (date && date.length > 0) {
      for (var i = 0; i < possibleFormats.length; i++) {
        // Was the date inputted in this possible format?
        if (!isNaN(parse(date, possibleFormats[i], new Date()).getTime())) {
          // Convert it to the expected format and return.
          return format(parse(date, possibleFormats[i], new Date()), expectedFormat);
        }
      }
    }
    // The date has been passed in using an unexpected format or is falsy.
    return null;
  }

  @Effect()
  bulkAddCustomersAndInviteFamilies = this.actions$.pipe(
    ofType(Customers.REQUEST_BULK_ADD_CUSTOMERS),
    map((action) =>
      action.payload.map((customerRow) => ({
        customerPayload: {
          externalId: customerRow.externalId,
          firstName: customerRow.firstName,
          lastName: customerRow.lastName,
          dateOfBirth: this.parseDate(customerRow.dateOfBirth),
          address: {
            address1: customerRow.address1,
            address2: customerRow.address2,
            town: customerRow.town,
            state: customerRow.state,
            postcode: customerRow.postcode,
            country: customerRow.country,
          },
        },
        familyInvites: [
          customerRow.family_Email,
          customerRow.family_Email1,
          customerRow.family_Email2,
          customerRow.family_Email3,
          customerRow.family_Email4,
          customerRow.family_Email5,
          customerRow.family_Email6,
          customerRow.family_Email7,
          customerRow.family_Email8,
          customerRow.family_Email9,
          customerRow.family_Email10,
        ],
      }))
    ),
    withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
    switchMap(async ([payload, organisationId]) => {
      const results = [];

      for (let i = 0; i < payload.length; i++) {
        const customerRow = payload[i];
        try {
          // create the customer
          const customerResult = await this.customerService
            .createCustomer({
              createCustomerRequestDto: { ...customerRow.customerPayload, organisationId },
            })
            .toPromise();

          // invite the family
          const filtered = customerRow.familyInvites.filter(
            (invite) => !!invite && invite.length > 0
          );
          const inviteResults = [];
          for (const invite of filtered) {
            const inviteResult = await this.invitesService
              .createInvitation({
                invitationRequestDto: {
                  email: invite,
                  role: FAMILY_ROLE,
                  organisationId,
                  permissions: [
                    {
                      modelId: customerResult.id,
                      modelName: 'Customer',
                      role: CUSTOMER_FAMILY_ROLE,
                    },
                  ],
                },
              })
              .toPromise();

            inviteResults.push(inviteResult);
          }
          results.push({
            payload: customerRow,
            response: customerResult,
            invites: inviteResults,
            hasError: false,
          });
        } catch (err) {
          results.push({
            payload: customerRow,
            errors: { [err.name]: err },
            hasErrors: true,
          });
        } finally {
          this.store.dispatch(Customers.ProgressBulkAddCustomers((i + 1) / payload.length));
        }
      }
      return Customers.ResponseSuccessBulkAddCustomers(results);
    })
  );

  @Effect()
  saveCustomer = this.actions$.pipe(
    ofType(Customers.REQUEST_SAVE),
    withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
    switchMap(([action, organisationId]) => {
      const details = { ...action.payload.data };
      if (!details.location || details.location.lat === '' || details.location.lng === '') {
        details.location = null;
      }
      return this.customerService
        .updateCustomer({ id: action.payload.id, updateCustomerRequestDto: details })
        .pipe(
          map((payload) => {
            return Customers.ResponseSuccessSaveAction(payload);
          }),
          catchError((err) => of(Customers.ResponseFailSaveAction(err)))
        );
    })
  );

  @Effect()
  requestCustomerAccess = this.actions$.pipe(
    ofType(Customers.REQUEST_ACCESS),
    mergeMap((action: any) =>
      this.customerService.requestAccess({ id: action.payload.id }).pipe(
        map((res) => Customers.ResponseSuccessAccess({ id: action.payload.id })),
        catchError((error) => of(Customers.ResponseFailAccess({ id: action.payload.id, error })))
      )
    )
  );

  @Effect()
  fetchMyCustomersAfterOrgChange = createEffect((): any =>
    this.actions$.pipe(
      ofType(Organisations.SELECT_BY_ID),
      withLatestFrom(this.store.select(org.currentOrgRoles)),
      filter(([action, roles]) => !!roles && roles.includes(CUSTOMER_MEMBER_ROLE)),
      map(() => Customers.RequestAllAction())
    )
  );

  addCustomerLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Customers.RequestAddLink),
      switchMap((action) => {
        const addUrlDto: AddUrlDto = {
          description: action.description,
          roles: action.roles,
          url: action.url,
        };
        return this.customerService.addUrl({ id: action.customerId, addUrlDto }).pipe(
          map((customer) =>
            Customers.ResponseSuccessAddLink({
              customer,
            })
          ),
          catchError((error) =>
            of(
              Customers.ResponseFailAddLink({
                customerId: action.customerId,
                error,
              })
            )
          )
        );
      })
    )
  );

  updateCustomerLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Customers.RequestUpdateLink),
      switchMap((action) => {
        return this.customerService
          .updateUrl({
            id: action.customerId,
            urlId: action.urlId,
            addUrlDto: { description: action.description, url: action.url, roles: action.roles },
          })
          .pipe(
            map((customer) =>
              Customers.ResponseSuccessUpdateLink({
                customer,
              })
            ),
            catchError((error) =>
              of(
                Customers.ResponseFailUpdateLink({
                  customerId: action.customerId,
                  urlId: action.urlId,
                  error,
                })
              )
            )
          );
      })
    )
  );

  removeCustomerLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Customers.RequestRemoveLink),
      switchMap((action) => {
        return this.customerService.removeUrl({ id: action.customerId, urlId: action.urlId }).pipe(
          map((customer) =>
            Customers.ResponseSuccessRemoveLink({
              customer,
            })
          ),
          catchError((error) =>
            of(
              Customers.ResponseFailRemoveLink({
                customerId: action.customerId,
                urlId: action.urlId,
                error,
              })
            )
          )
        );
      })
    )
  );
  logLinkView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Customers.RequestLogLinkView),
      switchMap((action) =>
        this.customerService.logUrlAccess({ customerId: action.customerId, id: action.urlId }).pipe(
          map((response) =>
            Customers.SuccessLogLinkView({ customerId: action.customerId, urlId: action.urlId })
          ),
          catchError((error) =>
            of(
              Customers.FailLogLinkView({
                customerId: action.customerId,
                urlId: action.urlId,
                error: error,
              })
            )
          )
        )
      )
    )
  );
  getLinkHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Customers.RequestLinkHistory),
      switchMap((action) =>
        this.customerService
          .getCustomerUrlHistory({ customerId: action.customerId, id: action.urlId })
          .pipe(
            map((response) =>
              Customers.SuccessLinkHistory({
                customerId: action.customerId,
                urlId: action.urlId,
                history: response,
              })
            ),
            catchError((error) =>
              of(
                Customers.FailLinkHistory({
                  customerId: action.customerId,
                  urlId: action.urlId,
                  error: error,
                })
              )
            )
          )
      )
    )
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<CAAction>,
    private customerService: CustomersService,
    private invitesService: InvitationsService,
    private locationService: GoogleGeocodeService
  ) {}

  /**
   * Attempts to add a location to each customer using the location service.
   * If fails, just continues without the location.
   *
   * @param customers The array of customers for the bulk import
   */
  private async addAddresses(customers: Array<{ address: any }>): Promise<any> {
    const toReturn = [];
    const BATCH_SIZE = 5;
    let pointer = 0;
    while (pointer <= customers.length) {
      const slice = customers.slice(pointer, pointer + BATCH_SIZE);
      await Promise.all(
        slice.map(async (customer) => {
          let location;
          try {
            location = await this.locationService.requestLocation(customer.address).toPromise();
          } catch (error) {
            location = { error };
          }
          if (!location.error) {
            return toReturn.push({
              ...customer,
              location,
            });
          } else {
            return toReturn.push(customer);
          }
        })
      );
      pointer += BATCH_SIZE;
    }
    return toReturn;
  }
}
